import { ReplaySubject } from 'rxjs';

import { OriginalInputFile, RotationAndFlip } from '../types/types';
import { ProcessorMetadataUtils } from '../utils/processor-metadata.utils';
import { StringUtils } from '../utils/string-utils';
import { FileUploadRequestMetadata } from './file-upload-request-metadata';

/** The `reason` on the terminal event a cancelled upload emits -- the value a consumer compares against. */
export const UPLOAD_CANCELLED_REASON = 'cancelled' as const;

/**
 * What `whenComplete()` rejects with -- and what the service's chunk-upload promise rejects with -- when
 * the USER stopped an upload (R14-SH14). Told apart from `setError`'s plain `Error('Upload failed')` by
 * `reason`, which `isUploadCancelled` reads instead of `instanceof` so a jest module mock of this file
 * (the manager's policy spec spreads `requireActual`) cannot break the check.
 */
export class UploadCancelledError extends Error {
    public readonly reason: typeof UPLOAD_CANCELLED_REASON = UPLOAD_CANCELLED_REASON;
    constructor(public readonly uuid: string) {
        super(`Upload cancelled ${uuid}`);
        this.name = 'UploadCancelledError';
    }
}

export function isUploadCancelled(e: unknown): e is UploadCancelledError {
    return !!e && (e as { reason?: unknown }).reason === UPLOAD_CANCELLED_REASON;
}

/**
 * A line under a staged row's name (prod fixes 2026-10-02, PLAN.md C6): FIX-2's "Uploading again…" /
 * "This upload expired…", FIX-6's "Can't convert …". Set only through `setStagedStatus`; absent on every
 * row today, which renders exactly as before.
 *
 * - `kind`: `info` and `warning` are told apart by icon plus a visually hidden "Note:" / "Warning:", never
 *   colour alone; `blocked` renders like `warning` AND leaves the row out of Convert (`convertEligible`).
 * - `live`: announce it through the row's pre-mounted polite region (the status arrived in the background);
 *   false renders it without announcing.
 * - `action`: one optional working button on the row (e.g. Remove), never a dead one.
 */
export interface StagedStatus {
    code: string;
    kind: 'info' | 'warning' | 'blocked';
    text: string;
    live: boolean;
    action?: { label: string; onClick: () => void };
}

/** `state` on a `changes` emission: one word per row; `cancelled` is the one R14-SH14 added. */
export type UploadInProgressState = 'uploading' | 'completed' | 'failed' | 'cancelled';

export namespace UploadInProgressFileFactory {
    export async function newUploadInProgressFile<T>(
        file: T,
        metadata: FileUploadRequestMetadata,
        uniqueProcessType: string,
    ): Promise<UploadInProgressFile<T>> {
        const uuid = StringUtils.uuid();
        return new UploadInProgressFile<T>(
            file,
            metadata,
            uniqueProcessType,
            uuid,
        );
    }
}

export class UploadInProgressFile<T> implements UploadInProgressFile<T> {
    public file: T;
    public metadata: FileUploadRequestMetadata;
    /**
     * UUID used in FileUpload cannot be file upload's ObjectId since we use the same FileUpload for re-edit functionality
     */
    public uuid: string;
    public progress: number;
    public completed: boolean;
    public error: boolean;
    /** Set once by `cancel()`; every setter below is a no-op after it, so observers see ONE terminal event. */
    public cancelled = false;
    private _cancelFn: (() => void) | null = null;
    private _retryFn: (() => Promise<void>) | null = null;
    private originalInputFile: OriginalInputFile;
    public orientation: RotationAndFlip;
    /** See [StagedStatus]. Null on every row until a fix sets one. */
    public stagedStatus: StagedStatus | null = null;

    private completePromiseResolver = null;
    private completePromiseRejector = null;
    private completePromise: Promise<any> = new Promise<UploadInProgressFile<any>>((resolve, reject) => {
        this.completePromiseResolver = resolve;
        this.completePromiseRejector = reject;
    });

    private responseReadyPromiseResolver = null;
    private responseReadyPromise: Promise<any> = new Promise<UploadInProgressFile<any>>(resolve => {
        this.responseReadyPromiseResolver = resolve;
    });

    public uniqueProcessType: string;

    private readonly _changes = new ReplaySubject<UploadInProgressFile<any>>(1);
    public readonly changes = this._changes.asObservable();

    get id() {
        return this.uuid;
    }

    get state(): UploadInProgressState {
        if (this.cancelled) return 'cancelled';
        if (this.error) return 'failed';
        if (this.completed) return 'completed';
        return 'uploading';
    }

    constructor(file: T, metadata: FileUploadRequestMetadata, uniqueProcessType: string, uuid: string) {
        this.file = file;
        this.metadata = metadata;
        this.uuid = uuid;
        this.progress = 0;
        this.completed = false;

        this.uniqueProcessType = uniqueProcessType;
    }

    getMetadata() {
        return this.metadata;
    }

    setMetadata(metadata: FileUploadRequestMetadata) {
        this.metadata = metadata;
    }


    getFile() {
        return this.file;
    }

    getProgress() {
        return this.progress;
    }

    setProgress(progress: number) {
        if (this.cancelled) return;
        this.progress = progress;
        this._changes.next(this);
    }

    getComplete() {
        return this.completed;
    }

    /**
     * Update the upload status. File might not still be ready
     */
    setComplete() {
        if (this.cancelled) return;
        this.completePromiseResolver(this);
        this.completed = true;
    }

    /**
     * File upload completed but might not ready
     */
    whenComplete(): Promise<UploadInProgressFile<any>> {
        return this.completePromise;
    }

    getError() {
        return this.error;
    }

    setError(retryFn?: () => Promise<void>) {
        if (this.cancelled) return;
        this.error = true;
        this._retryFn = retryFn || null;
        this._changes.next(this);
        if (this.completePromiseRejector) {
            this.completePromiseRejector(new Error('Upload failed'));
            this.completePromiseRejector = null;
        }
    }

    get canRetry(): boolean {
        return this.error && this._retryFn !== null;
    }

    /**
     * Upload this row again in place (fu-r2). FU2-01 (fu-r3): `setError` rejected the one-shot `whenComplete()`
     * promise, so after a successful retry nothing waiting on it ever heard the upload finish -- the card's file
     * details (duration, bitrate, size) stayed skeleton bars. The row gets a fresh promise first, so `whenComplete()`
     * after this call is the NEW attempt's; the `changes` emission below is how a consumer learns to re-read it.
     */
    async retry(): Promise<void> {
        if (!this._retryFn) return;
        this.error = false;
        this.progress = 0;
        this.rearmCompletePromise();
        this._changes.next(this);
        await this._retryFn();
    }

    /**
     * A new pending `whenComplete()` once the old one was rejected (`setError` clears the rejector). A promise still
     * pending is kept. The new one is marked handled here: a retry that fails again rejects it, and a row nobody waits
     * on must not raise an unhandled rejection for that (every `.then` / `.catch` a consumer attaches still sees it).
     */
    private rearmCompletePromise() {
        if (this.completePromiseRejector) return;
        this.completePromise = new Promise<UploadInProgressFile<any>>((resolve, reject) => {
            this.completePromiseResolver = resolve;
            this.completePromiseRejector = reject;
        });
        this.completePromise.catch(() => undefined);
    }

    /**
     * Given by `FileUploadService` once a Resumable exists for this file (re-bound on every attempt, since
     * each attempt is a new Resumable). Absent for the single-request, URL and Drive paths, where
     * `cancel()` still marks the row but has no transfer to stop.
     */
    bindCancel(cancelFn: () => void) {
        this._cancelFn = cancelFn;
    }

    /**
     * Stop this upload (R14-SH14). True when it was in flight and is now `cancelled`; false -- and nothing
     * happens -- for a second call or for a finished upload (transfer complete or response in hand).
     *
     * Terminal event, once: `changes` emits this file with `state === 'cancelled'`, `whenComplete()` rejects
     * with an `UploadCancelledError` (`reason: 'cancelled'`), the retry closure is dropped, and every later
     * `setProgress` / `setComplete` / `setError` / `setResponse` from a transport still winding down is
     * ignored. `error` stays false: a cancel is the user's decision, not a failure.
     */
    cancel(): boolean {
        if (this.cancelled || this.completed || this.originalInputFile) return false;
        this.cancelled = true;
        this._retryFn = null;
        const abort = this._cancelFn;
        this._cancelFn = null;
        if (abort) {
            try {
                abort();
            } catch {
                // The transfer may already be gone; the row is cancelled regardless.
            }
        }
        this._changes.next(this);
        if (this.completePromiseRejector) {
            this.completePromiseRejector(new UploadCancelledError(this.uuid));
            this.completePromiseRejector = null;
        }
        return true;
    }

    getResponse(): OriginalInputFile {
        return this.originalInputFile;
    }

    setResponse(originalInputFile: OriginalInputFile) {
        if (this.cancelled) return;
        this.originalInputFile = originalInputFile;
        this._changes.next(this);
        this.responseReadyPromiseResolver(this);
    }

    /**
     * File is complete and ready. 
     */
    whenResponseReady(): Promise<UploadInProgressFile<any>> {
        return this.responseReadyPromise;
    }

    /** Sets (or, with null, clears) the row's status line and tells the row to re-render. */
    setStagedStatus(status: StagedStatus | null) {
        this.stagedStatus = status;
        this._changes.next(this);
    }

    setOrientation(orientation: RotationAndFlip) {
        this.orientation = orientation;
        this._changes.next(this);
    }

    getOrientation() {
        return this.orientation;
    }

    /**
     * FIX-2 (UX-SPEC §2.1): the edge deleted this row's upload, and the bytes are still in this tab. The row goes back
     * to "uploading" so `getComplete()` holds Convert until the new upload's `setComplete`; the old response is kept
     * until `setResponse` replaces it. The caller (the upload manager) runs the upload.
     */
    prepareReupload() {
        if (this.cancelled) return;
        this.completed = false;
        this.error = false;
        this.progress = 0;
        this._changes.next(this);
    }

    getCommURL(): Promise<string> {
        return this.whenComplete().then(() => {
            return ProcessorMetadataUtils.getUploadCommEndpoint(this.metadata.getProcessorMetadata(0));
        });
    }
}

/**
 * FIX-6 (UX-SPEC section 6) additions to [StagedStatus], declared apart from it so the shared contract block
 * stays byte-identical: `title` is a visible lead-in rendered before `text` ("Can't convert"), `link` turns the
 * first occurrence of `link.text` inside `text` into a plain link that opens in a new tab ("ruffle.rs").
 */
export interface StagedStatus {
    title?: string;
    link?: { text: string; href: string };
}
